import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ErrorBanner } from '@/ui/Alert';
import { Button } from '@/ui/Button';
import { PageSpinner } from '@/ui/Spinner';
import { useSession } from './session';
import styles from './RequireAuth.module.css';

/** Renders its children only for a signed-in admin; otherwise sends them to sign in. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, isPending, error, refetch } = useSession();
  const location = useLocation();

  if (isPending) return <PageSpinner label="Checking your session" />;

  if (error && !session) {
    return (
      <div className={styles.failure}>
        <ErrorBanner
          error={error}
          action={
            <Button size="small" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  if (!session) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  // A password the server considers expired blocks everything else until it is changed.
  if (session.must_change_password && location.pathname !== '/account/password') {
    return <Navigate to="/account/password" replace />;
  }

  return children;
}
